import React, { useState, useCallback, useEffect } from "react";

const UseFormFileInput = ({
  title,
  field,
  setValue,
  error,
  disable,
  isbase64 = false,
  multiple = false,
  accept = null,
  dragAndDrop = false, // ✅ NEW FLAG
  files = null,
}) => {
  const [fileName, setFileName] = useState("");
  const [fileNames, setFileNames] = useState([]);
  const [isDragging, setIsDragging] = useState(false);

  useEffect(() => {
    if (!files) return;

    if (multiple) {
      const fileArray = Array.from(files);
      setFileNames(fileArray.map((item) => item.name));
    } else {
      setFileName(files?.name || "");
    }
  }, [field, multiple, files]);

  const processFiles = useCallback(
    async (files = []) => {
      if (!files.length) return;

      const fileArray = Array.from(files);
      const file = fileArray[0];

      if (multiple) {
        setValue(field, fileArray);
        setFileNames(fileArray.map((item) => item.name));
      } else {
        setValue(field, file);
        setFileName(file?.name || "");
      }

      if (file && isbase64 && !multiple) {
        const base64String = await convertToBase64(file);
        setValue(field, {
          file,
          base64: base64String,
        });
      }
    },
    [field, isbase64, multiple, setValue],
  );

  const handleFileChange = async (e) => {
    await processFiles(e.target.files);
  };

  // =======================
  // Drag & Drop Handlers
  // =======================
  const handleDragOver = (e) => {
    if (!dragAndDrop || disable) return;
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    if (!dragAndDrop) return;
    setIsDragging(false);
  };

  const handleDrop = async (e) => {
    if (!dragAndDrop || disable) return;
    e.preventDefault();
    setIsDragging(false);
    await processFiles(e.dataTransfer.files);
  };

  const hasFiles = multiple ? fileNames.length > 0 : fileName.length > 0;

  return (
    <div className="mb-4">
      <div
        className={`border border-2 rounded-4 p-4 text-center bg-light position-relative upload-box ${
          isDragging ? "drag-active" : ""
        }`}
        style={{
          cursor: disable ? "not-allowed" : "pointer",
          transition: "0.3s",
        }}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        {/* Invisible file input */}
        <input
          type="file"
          id={field}
          name={field}
          className="d-none"
          multiple={multiple}
          onChange={handleFileChange}
          disabled={disable}
          accept={accept}
        />

        {/* Label inside the upload area */}
        <label htmlFor={field} className="w-100 h-100">
          <div className="d-flex flex-column align-items-center justify-content-center">
            <i className="bi bi-cloud-upload fs-1 text-primary mb-2"></i>

            <span className="fw-semibold text-primary">
              {hasFiles
                ? `Click to Change ${multiple ? "Files" : "File"}`
                : title}
            </span>

            <small className="text-muted mt-1">
              {hasFiles
                ? multiple
                  ? `Selected: ${fileNames.length} file${
                      fileNames.length !== 1 ? "s" : ""
                    }`
                  : `Selected: ${fileName}`
                : dragAndDrop
                  ? `Drop ${multiple ? "files" : "file"} or click to upload`
                  : `Click to upload ${multiple ? "files" : "file"}`}
            </small>
          </div>
        </label>
      </div>

      {error && (
        <div
          className="text-error mt-2 fw-medium"
          style={{ fontSize: "0.85rem" }}
        >
          {error}
        </div>
      )}

      <style>{`
        .upload-box:hover {
          background: #f8f9fa;
          border-color: #0d6efd;
          box-shadow: 0 0 10px rgba(13, 110, 253, 0.2);
        }

        .upload-box.drag-active {
          background: #e9f2ff;
          border-color: #0d6efd;
          box-shadow: 0 0 15px rgba(13, 110, 253, 0.35);
        }
      `}</style>
    </div>
  );
};

export default UseFormFileInput;

const convertToBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);

    reader.onload = () => resolve(reader.result);
    reader.onerror = (error) => reject(error);
  });
};
